import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import {
    LogOut,
    PencilLine,
    Plus,
    Star,
    Upload,
    Trash2,
    Eye,
    EyeOff,
    Loader2,
    CheckCircle2,
    AlertTriangle,
} from "lucide-react";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VIDEO_MAX_BYTES = 250 * 1024 * 1024;

const STATUS_OPTIONS = [
    { value: "coming_soon", label: "Coming Soon" },
    { value: "draft", label: "Draft" },
    { value: "published", label: "Published" },
    { value: "archived", label: "Archived" },
];

const STATUS_STYLE = {
    coming_soon: "border-amber-500/50 bg-amber-500/10 text-amber-100",
    draft: "border-slate-500/50 bg-slate-500/10 text-slate-100",
    published: "border-emerald-500/50 bg-emerald-500/10 text-emerald-100",
    archived: "border-rose-500/50 bg-rose-500/10 text-rose-100",
};

function formatBytes(n) {
    if (!n) return "—";
    const mb = n / (1024 * 1024);
    if (mb < 1) return `${(n / 1024).toFixed(0)} KB`;
    return `${mb.toFixed(1)} MB`;
}

function VideoEditModal({ video, categories, onClose, onSaved }) {
    const [form, setForm] = useState({
        title: video.title || "",
        description: video.description || "",
        category_slug: video.category_slug || "",
        category_label: video.category_label || "",
        cta_label: video.cta_label || "Speak With an Agent",
        cta_link: video.cta_link || "/contact",
        public_url: video.public_url || "",
        tags: (video.tags || []).join(", "),
        status: video.status || "draft",
        featured: !!video.featured,
        sort_order: video.sort_order ?? 0,
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    function onChange(field) {
        return (e) => {
            const val =
                e.target.type === "checkbox"
                    ? e.target.checked
                    : e.target.type === "number"
                    ? Number(e.target.value)
                    : e.target.value;
            setForm((f) => ({ ...f, [field]: val }));
        };
    }

    function onCategoryChange(e) {
        const slug = e.target.value;
        const found = categories.find((c) => c.slug === slug);
        setForm((f) => ({
            ...f,
            category_slug: slug,
            category_label: found ? found.label : f.category_label,
        }));
    }

    async function onSubmit(e) {
        e.preventDefault();
        setSaving(true);
        setError("");
        try {
            const payload = {
                ...form,
                tags: form.tags
                    .split(",")
                    .map((t) => t.trim())
                    .filter(Boolean),
            };
            const { data } = await axios.put(
                `${API}/admin/videos/${video.id}`,
                payload
            );
            onSaved(data);
        } catch (err) {
            setError(
                err?.response?.data?.detail || "Couldn't save. Please retry."
            );
        } finally {
            setSaving(false);
        }
    }

    return (
        <div
            data-testid="video-edit-modal"
            className="fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-black/85 p-4 backdrop-blur-sm"
            onClick={onClose}
        >
            <div
                className="w-full max-w-2xl rounded-2xl border border-[rgba(212,175,55,0.35)] bg-[#0b0e13]"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between border-b border-[rgba(212,175,55,0.2)] px-6 py-4">
                    <h2 className="font-serif text-xl text-brand-ivory">
                        Edit video
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-xs uppercase tracking-[0.2em] text-brand-muted hover:text-brand-ivory"
                    >
                        Close
                    </button>
                </div>
                <form
                    onSubmit={onSubmit}
                    className="space-y-4 px-6 py-5 text-brand-ivory"
                >
                    <p className="text-xs text-brand-muted">
                        Slug:{" "}
                        <code className="text-brand-gold2">{video.slug}</code>
                        {" · "}URL is locked so public bookmarks never break.
                    </p>
                    <Field label="Title">
                        <input
                            data-testid="edit-title"
                            required
                            value={form.title}
                            onChange={onChange("title")}
                            className="enc-input"
                        />
                    </Field>
                    <Field label="Description">
                        <textarea
                            data-testid="edit-description"
                            rows={3}
                            value={form.description}
                            onChange={onChange("description")}
                            className="enc-input"
                        />
                    </Field>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Field label="Category">
                            <select
                                data-testid="edit-category"
                                value={form.category_slug}
                                onChange={onCategoryChange}
                                className="enc-input"
                            >
                                {categories.map((c) => (
                                    <option key={c.slug} value={c.slug}>
                                        {c.label}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Status">
                            <select
                                data-testid="edit-status"
                                value={form.status}
                                onChange={onChange("status")}
                                className="enc-input"
                            >
                                {STATUS_OPTIONS.map((s) => (
                                    <option key={s.value} value={s.value}>
                                        {s.label}
                                    </option>
                                ))}
                            </select>
                        </Field>
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Field label="CTA label">
                            <input
                                value={form.cta_label}
                                onChange={onChange("cta_label")}
                                className="enc-input"
                            />
                        </Field>
                        <Field label="CTA link (path or URL)">
                            <input
                                value={form.cta_link}
                                onChange={onChange("cta_link")}
                                className="enc-input"
                            />
                        </Field>
                    </div>
                    <Field label="Tags (comma-separated)">
                        <input
                            value={form.tags}
                            onChange={onChange("tags")}
                            className="enc-input"
                        />
                    </Field>
                    <Field label="Same-origin streaming URL (optional)">
                        <input
                            data-testid="admin-video-public-url"
                            name="public_url"
                            value={form.public_url}
                            onChange={onChange("public_url")}
                            className="enc-input"
                            placeholder="e.g. /api/video-asset/my-video.mp4?v=1 — leave blank to use Dropbox upload"
                        />
                    </Field>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Field label="Sort order (lower = first)">
                            <input
                                type="number"
                                value={form.sort_order}
                                onChange={onChange("sort_order")}
                                className="enc-input"
                            />
                        </Field>
                        <label className="flex items-center gap-3 self-end rounded-md border border-[rgba(212,175,55,0.25)] bg-[#0d1117] px-3 py-2.5">
                            <input
                                type="checkbox"
                                data-testid="edit-featured"
                                checked={form.featured}
                                onChange={onChange("featured")}
                            />
                            <span className="text-sm">Featured on public page</span>
                        </label>
                    </div>
                    {error && (
                        <p className="rounded-md border border-red-700/50 bg-red-900/20 px-3 py-2 text-xs text-red-100">
                            {error}
                        </p>
                    )}
                    <div className="flex items-center justify-end gap-3 border-t border-[rgba(212,175,55,0.15)] pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-full border border-[rgba(212,175,55,0.3)] px-4 py-2 text-xs uppercase tracking-[0.2em] text-brand-ivory hover:border-brand-gold hover:text-brand-gold2"
                        >
                            Cancel
                        </button>
                        <button
                            data-testid="edit-save"
                            type="submit"
                            disabled={saving}
                            className="enc-submit inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm disabled:opacity-60"
                        >
                            {saving && (
                                <Loader2
                                    aria-hidden="true"
                                    className="h-4 w-4 animate-spin"
                                />
                            )}
                            Save changes
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function VideoCreateModal({ categories, onClose, onCreated }) {
    const [form, setForm] = useState({
        slug: "",
        title: "",
        description: "",
        category_slug: categories[0]?.slug || "",
        category_label: categories[0]?.label || "",
        cta_label: "Speak With an Agent",
        cta_link: "/contact",
        tags: "",
        status: "draft",
        featured: false,
        sort_order: 50,
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    function onChange(field) {
        return (e) => {
            const val =
                e.target.type === "checkbox"
                    ? e.target.checked
                    : e.target.type === "number"
                    ? Number(e.target.value)
                    : e.target.value;
            setForm((f) => ({ ...f, [field]: val }));
        };
    }

    function onCategoryChange(e) {
        const slug = e.target.value;
        const found = categories.find((c) => c.slug === slug);
        setForm((f) => ({
            ...f,
            category_slug: slug,
            category_label: found ? found.label : f.category_label,
        }));
    }

    function onTitleChange(e) {
        const title = e.target.value;
        setForm((f) => ({
            ...f,
            title,
            // Auto-generate slug from title if the user hasn't typed one manually.
            slug:
                !f.slug || f._slugAuto
                    ? title
                          .toLowerCase()
                          .replace(/[^a-z0-9]+/g, "-")
                          .replace(/^-+|-+$/g, "")
                          .slice(0, 60)
                    : f.slug,
            _slugAuto: true,
        }));
    }

    async function onSubmit(e) {
        e.preventDefault();
        if (!form.slug || !form.title || !form.description || !form.category_slug) {
            setError("Slug, title, description, and category are required.");
            return;
        }
        setSaving(true);
        setError("");
        try {
            const payload = {
                slug: form.slug,
                title: form.title,
                description: form.description,
                category_slug: form.category_slug,
                category_label: form.category_label,
                cta_label: form.cta_label,
                cta_link: form.cta_link,
                tags: form.tags
                    .split(",")
                    .map((t) => t.trim())
                    .filter(Boolean),
                status: form.status,
                featured: form.featured,
                sort_order: form.sort_order,
            };
            const { data } = await axios.post(`${API}/admin/videos`, payload);
            onCreated(data);
        } catch (err) {
            setError(
                err?.response?.data?.detail ||
                    "Couldn't create the record. Please retry."
            );
        } finally {
            setSaving(false);
        }
    }

    return (
        <div
            data-testid="video-create-modal"
            className="fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-black/85 p-4 backdrop-blur-sm"
            onClick={onClose}
        >
            <div
                className="w-full max-w-2xl rounded-2xl border border-[rgba(212,175,55,0.35)] bg-[#0b0e13]"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between border-b border-[rgba(212,175,55,0.2)] px-6 py-4">
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.3em] text-brand-goldSoft">
                            New Video
                        </p>
                        <h2 className="font-serif text-xl text-brand-ivory">
                            Add a new education video
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-xs uppercase tracking-[0.2em] text-brand-muted hover:text-brand-ivory"
                    >
                        Close
                    </button>
                </div>
                <form
                    onSubmit={onSubmit}
                    className="space-y-4 px-6 py-5 text-brand-ivory"
                >
                    <p className="text-xs text-brand-muted">
                        Creates a new card under the selected category. After
                        saving, upload the MP4 from the row in the main table.
                        Cards default to <em>Draft</em>; switch to{" "}
                        <em>Published</em> after the MP4 is uploaded.
                    </p>
                    <Field label="Title">
                        <input
                            data-testid="create-title"
                            required
                            value={form.title}
                            onChange={onTitleChange}
                            className="enc-input"
                            placeholder="e.g. Flood vs. Wind: What Coastal Owners Need to Know"
                        />
                    </Field>
                    <Field label="URL slug (lower-case, hyphens) — locks the public URL forever">
                        <input
                            data-testid="create-slug"
                            required
                            value={form.slug}
                            onChange={(e) => {
                                const v = e.target.value
                                    .toLowerCase()
                                    .replace(/[^a-z0-9-]/g, "-");
                                setForm((f) => ({
                                    ...f,
                                    slug: v,
                                    _slugAuto: false,
                                }));
                            }}
                            className="enc-input"
                            placeholder="flood-vs-wind"
                        />
                    </Field>
                    <Field label="Description">
                        <textarea
                            data-testid="create-description"
                            required
                            rows={3}
                            value={form.description}
                            onChange={onChange("description")}
                            className="enc-input"
                        />
                    </Field>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Field label="Category">
                            <select
                                data-testid="create-category"
                                value={form.category_slug}
                                onChange={onCategoryChange}
                                className="enc-input"
                            >
                                {categories.map((c) => (
                                    <option key={c.slug} value={c.slug}>
                                        {c.label}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Status">
                            <select
                                data-testid="create-status"
                                value={form.status}
                                onChange={onChange("status")}
                                className="enc-input"
                            >
                                {STATUS_OPTIONS.map((s) => (
                                    <option key={s.value} value={s.value}>
                                        {s.label}
                                    </option>
                                ))}
                            </select>
                        </Field>
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Field label="CTA label">
                            <input
                                value={form.cta_label}
                                onChange={onChange("cta_label")}
                                className="enc-input"
                            />
                        </Field>
                        <Field label="CTA link (path or URL)">
                            <input
                                value={form.cta_link}
                                onChange={onChange("cta_link")}
                                className="enc-input"
                            />
                        </Field>
                    </div>
                    <Field label="Tags (comma-separated)">
                        <input
                            value={form.tags}
                            onChange={onChange("tags")}
                            className="enc-input"
                            placeholder="flood, hurricane, coastal"
                        />
                    </Field>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Field label="Sort order (lower = first)">
                            <input
                                type="number"
                                value={form.sort_order}
                                onChange={onChange("sort_order")}
                                className="enc-input"
                            />
                        </Field>
                        <label className="flex items-center gap-3 self-end rounded-md border border-[rgba(212,175,55,0.25)] bg-[#0d1117] px-3 py-2.5">
                            <input
                                type="checkbox"
                                checked={form.featured}
                                onChange={onChange("featured")}
                            />
                            <span className="text-sm">
                                Featured on public page
                            </span>
                        </label>
                    </div>
                    {error && (
                        <p
                            data-testid="create-error"
                            className="rounded-md border border-red-700/50 bg-red-900/20 px-3 py-2 text-xs text-red-100"
                        >
                            {error}
                        </p>
                    )}
                    <div className="flex items-center justify-end gap-3 border-t border-[rgba(212,175,55,0.15)] pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-full border border-[rgba(212,175,55,0.3)] px-4 py-2 text-xs uppercase tracking-[0.2em] text-brand-ivory hover:border-brand-gold hover:text-brand-gold2"
                        >
                            Cancel
                        </button>
                        <button
                            data-testid="create-save"
                            type="submit"
                            disabled={saving}
                            className="enc-submit inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm disabled:opacity-60"
                        >
                            {saving && (
                                <Loader2
                                    aria-hidden="true"
                                    className="h-4 w-4 animate-spin"
                                />
                            )}
                            Create video
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block text-sm">
            <span className="text-[10px] uppercase tracking-[0.25em] text-brand-goldSoft">
                {label}
            </span>
            <div className="mt-1">{children}</div>
        </label>
    );
}

function UploadRow({ video, onUploaded }) {
    const inputRef = useRef(null);
    const [progress, setProgress] = useState(null); // 0-100 or null
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    function pickFile() {
        setError("");
        setSuccess("");
        inputRef.current?.click();
    }

    async function onFileChosen(e) {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(".mp4")) {
            setError("Only .mp4 files are accepted.");
            return;
        }
        if (file.size > VIDEO_MAX_BYTES) {
            setError(
                `File is ${formatBytes(
                    file.size
                )} — the limit is ${formatBytes(VIDEO_MAX_BYTES)}.`
            );
            return;
        }
        try {
            setProgress(0);
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await axios.post(
                `${API}/admin/videos/${video.id}/upload`,
                fd,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    onUploadProgress: (evt) => {
                        if (evt.total) {
                            setProgress(
                                Math.round((evt.loaded / evt.total) * 100)
                            );
                        }
                    },
                }
            );
            setSuccess(
                `Uploaded ${formatBytes(data.size_bytes)} — ready to publish.`
            );
            onUploaded(video.id, data);
        } catch (err) {
            setError(
                err?.response?.data?.detail ||
                    "Upload failed. Please try again."
            );
        } finally {
            setProgress(null);
        }
    }

    return (
        <div className="flex items-center gap-3">
            <button
                data-testid={`upload-${video.slug}`}
                type="button"
                onClick={pickFile}
                disabled={progress !== null}
                className="inline-flex items-center gap-2 rounded-full border border-brand-gold/60 bg-[#0b0e13] px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] text-brand-gold2 transition-colors hover:bg-brand-gold hover:text-brand-bg disabled:opacity-60"
            >
                <Upload aria-hidden="true" className="h-3 w-3" />
                {video.dropbox_path ? "Replace MP4" : "Upload MP4"}
            </button>
            <input
                ref={inputRef}
                type="file"
                accept="video/mp4,.mp4"
                onChange={onFileChosen}
                className="hidden"
            />
            {progress !== null && (
                <div className="flex min-w-[120px] items-center gap-2 text-xs text-brand-muted">
                    <div className="h-1.5 w-24 overflow-hidden rounded-full bg-[#0d1117]">
                        <div
                            className="h-full bg-brand-gold transition-all"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    <span>{progress}%</span>
                </div>
            )}
            {error && (
                <span className="inline-flex items-center gap-1 text-xs text-red-300">
                    <AlertTriangle aria-hidden="true" className="h-3 w-3" />
                    {error}
                </span>
            )}
            {success && (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-300">
                    <CheckCircle2 aria-hidden="true" className="h-3 w-3" />
                    {success}
                </span>
            )}
        </div>
    );
}

export default function AdminVideosPage() {
    const { admin, loading, logout } = useAuth();
    const navigate = useNavigate();
    const [videos, setVideos] = useState([]);
    const [categories, setCategories] = useState([]);
    const [pageLoading, setPageLoading] = useState(true);
    const [pageError, setPageError] = useState("");
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);

    useEffect(() => {
        document.title = "Video Admin — Endurance Casualty";
    }, []);

    async function reload() {
        try {
            const [vRes, cRes] = await Promise.all([
                axios.get(`${API}/admin/videos`),
                axios.get(`${API}/videos/categories`),
            ]);
            setVideos(Array.isArray(vRes.data) ? vRes.data : []);
            setCategories(Array.isArray(cRes.data) ? cRes.data : []);
        } catch (err) {
            setPageError(
                err?.response?.data?.detail ||
                    "Couldn't load the video catalog."
            );
        } finally {
            setPageLoading(false);
        }
    }

    useEffect(() => {
        if (admin) reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [admin]);

    const sorted = useMemo(
        () =>
            [...videos].sort(
                (a, b) =>
                    Number(!!b.featured) - Number(!!a.featured) ||
                    (a.sort_order || 0) - (b.sort_order || 0)
            ),
        [videos]
    );

    if (!loading && !admin) return <Navigate to="/admin/login" replace />;

    async function quickSetStatus(video, newStatus) {
        try {
            const { data } = await axios.put(
                `${API}/admin/videos/${video.id}`,
                { status: newStatus }
            );
            setVideos((arr) =>
                arr.map((v) => (v.id === video.id ? { ...v, ...data } : v))
            );
        } catch (_) {
            /* the interceptor handles 401; brief noop is fine for other errors */
        }
    }

    async function toggleFeatured(video) {
        try {
            const { data } = await axios.put(
                `${API}/admin/videos/${video.id}`,
                { featured: !video.featured }
            );
            setVideos((arr) =>
                arr.map((v) => (v.id === video.id ? { ...v, ...data } : v))
            );
        } catch (_) {
            /* noop */
        }
    }

    async function archiveVideo(video) {
        if (
            !window.confirm(
                `Archive "${video.title}"? It will be hidden from the public library — you can restore it later by changing its status back.`
            )
        )
            return;
        try {
            await axios.delete(`${API}/admin/videos/${video.id}`);
            setVideos((arr) =>
                arr.map((v) =>
                    v.id === video.id ? { ...v, status: "archived" } : v
                )
            );
        } catch (_) {
            /* noop */
        }
    }

    return (
        <main
            data-testid="admin-videos-page"
            className="min-h-screen bg-brand-bg text-brand-ivory"
        >
            <header className="sticky top-0 z-30 border-b border-[rgba(212,175,55,0.2)] bg-[#0b0e13]/95 backdrop-blur">
                <div className="mx-auto flex max-w-[1400px] flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 py-4 sm:px-6">
                    <div className="flex min-w-0 items-center gap-2 sm:gap-3">
                        <Link
                            to="/"
                            className="text-[10px] uppercase tracking-[0.3em] text-brand-goldSoft hover:text-brand-gold2 sm:text-xs"
                        >
                            Endurance Casualty
                        </Link>
                        <span className="text-brand-muted">›</span>
                        <span className="font-serif text-base sm:text-lg">
                            Video Admin
                        </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                        <Link
                            to="/admin/blog"
                            data-testid="admin-link-blog"
                            className="rounded-full border border-[rgba(212,175,55,0.3)] px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-brand-gold2 hover:border-brand-gold hover:text-brand-gold sm:text-[11px]"
                        >
                            Blog →
                        </Link>
                        <Link
                            to="/admin/integrations"
                            data-testid="admin-link-integrations"
                            className="rounded-full border border-[rgba(212,175,55,0.3)] px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-brand-gold2 hover:border-brand-gold hover:text-brand-gold sm:text-[11px]"
                        >
                            Integrations →
                        </Link>
                        <Link
                            to="/admin/analytics"
                            data-testid="admin-link-analytics"
                            className="rounded-full border border-[rgba(212,175,55,0.3)] px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-brand-gold2 hover:border-brand-gold hover:text-brand-gold sm:text-[11px]"
                        >
                            Analytics →
                        </Link>
                        <Link
                            to="/admin/portal-users"
                            data-testid="admin-link-portal-users"
                            className="rounded-full border border-[rgba(212,175,55,0.3)] px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-brand-gold2 hover:border-brand-gold hover:text-brand-gold sm:text-[11px]"
                        >
                            Portal Users →
                        </Link>
                        <Link
                            to="/insurance-education-videos"
                            target="_blank"
                            className="rounded-full border border-[rgba(212,175,55,0.3)] px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-brand-ivory hover:border-brand-gold hover:text-brand-gold2 sm:text-[11px]"
                        >
                            View public page
                        </Link>
                        <span className="hidden truncate text-xs text-brand-muted lg:inline">
                            {admin?.email}
                        </span>
                        <button
                            data-testid="admin-logout"
                            onClick={() => {
                                logout();
                                navigate("/admin/login", { replace: true });
                            }}
                            className="inline-flex items-center gap-2 rounded-full border border-[rgba(212,175,55,0.3)] px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-brand-ivory hover:border-brand-gold hover:text-brand-gold2 sm:text-[11px]"
                        >
                            <LogOut aria-hidden="true" className="h-3 w-3" />
                            Sign out
                        </button>
                    </div>
                </div>
            </header>

            <section className="mx-auto max-w-[1400px] px-6 py-10">
                <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.35em] text-brand-goldSoft">
                            Education Library
                        </p>
                        <h1 className="mt-2 font-serif text-3xl">
                            Manage videos
                        </h1>
                        <p className="mt-3 max-w-2xl text-sm text-brand-muted">
                            Upload short educational MP4s (ideal length 30
                            seconds to 5 minutes), then toggle each card
                            between <em>Draft</em>, <em>Published</em>, and{" "}
                            <em>Archived</em>. Replacing a video&apos;s MP4
                            keeps the same public card and URL intact.
                        </p>
                    </div>
                    <button
                        type="button"
                        data-testid="admin-add-video"
                        onClick={() => setCreating(true)}
                        className="enc-submit inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs uppercase tracking-[0.2em]"
                    >
                        <Plus aria-hidden="true" className="h-4 w-4" />
                        New video
                    </button>
                </div>

                {pageLoading && (
                    <p
                        data-testid="admin-loading"
                        className="text-sm text-brand-muted"
                    >
                        Loading…
                    </p>
                )}
                {pageError && (
                    <p
                        data-testid="admin-error"
                        className="rounded-md border border-red-700/50 bg-red-900/20 px-3 py-2 text-sm text-red-100"
                    >
                        {pageError}
                    </p>
                )}

                {!pageLoading && !pageError && (
                    <div
                        data-testid="admin-videos-table"
                        className="overflow-x-auto rounded-2xl border border-[rgba(212,175,55,0.2)] bg-[#0b0e13]"
                    >
                        <table className="w-full min-w-[840px] table-fixed text-left text-sm">
                            <thead className="bg-[#0d1117] text-[10px] uppercase tracking-[0.25em] text-brand-goldSoft">
                                <tr>
                                    <th className="w-[28%] px-6 py-3">Title</th>
                                    <th className="w-[18%] px-3 py-3">
                                        Category
                                    </th>
                                    <th className="w-[14%] px-3 py-3">
                                        Status
                                    </th>
                                    <th className="w-[10%] px-3 py-3">File</th>
                                    <th className="w-[10%] px-3 py-3">
                                        Featured
                                    </th>
                                    <th className="w-[20%] px-6 py-3 text-right">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[rgba(212,175,55,0.12)]">
                                {sorted.map((v) => (
                                    <tr
                                        key={v.id}
                                        data-testid={`admin-row-${v.slug}`}
                                        className="align-top"
                                    >
                                        <td className="px-6 py-4">
                                            <p className="font-serif text-base text-brand-ivory">
                                                {v.title}
                                            </p>
                                            <p className="mt-1 truncate text-[11px] text-brand-muted">
                                                /{v.slug}
                                            </p>
                                        </td>
                                        <td className="px-3 py-4 text-xs text-brand-ivory/85">
                                            {v.category_label}
                                        </td>
                                        <td className="px-3 py-4">
                                            <select
                                                data-testid={`status-${v.slug}`}
                                                value={v.status}
                                                onChange={(e) =>
                                                    quickSetStatus(
                                                        v,
                                                        e.target.value
                                                    )
                                                }
                                                className={`w-full rounded-full border bg-[#0d1117] px-2 py-1 text-[10px] uppercase tracking-[0.2em] ${
                                                    STATUS_STYLE[v.status] ||
                                                    "border-slate-500/40 text-slate-100"
                                                }`}
                                            >
                                                {STATUS_OPTIONS.map((s) => (
                                                    <option
                                                        key={s.value}
                                                        value={s.value}
                                                    >
                                                        {s.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                        <td className="px-3 py-4 text-[11px] text-brand-muted">
                                            {v.dropbox_path
                                                ? formatBytes(v.size_bytes)
                                                : "—"}
                                        </td>
                                        <td className="px-3 py-4">
                                            <button
                                                data-testid={`feature-${v.slug}`}
                                                type="button"
                                                onClick={() => toggleFeatured(v)}
                                                aria-label={
                                                    v.featured
                                                        ? "Unfeature"
                                                        : "Mark as featured"
                                                }
                                                className={`inline-flex h-8 w-8 items-center justify-center rounded-full border transition-colors ${
                                                    v.featured
                                                        ? "border-brand-gold bg-brand-gold/15 text-brand-gold"
                                                        : "border-[rgba(212,175,55,0.3)] text-brand-muted hover:border-brand-gold hover:text-brand-gold2"
                                                }`}
                                            >
                                                <Star
                                                    aria-hidden="true"
                                                    className={`h-4 w-4 ${
                                                        v.featured
                                                            ? "fill-brand-gold"
                                                            : ""
                                                    }`}
                                                />
                                            </button>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col items-end gap-2">
                                                <UploadRow
                                                    video={v}
                                                    onUploaded={() => reload()}
                                                />
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        data-testid={`edit-${v.slug}`}
                                                        type="button"
                                                        onClick={() =>
                                                            setEditing(v)
                                                        }
                                                        className="inline-flex items-center gap-1 rounded-full border border-[rgba(212,175,55,0.3)] px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-brand-ivory hover:border-brand-gold hover:text-brand-gold2"
                                                    >
                                                        <PencilLine
                                                            aria-hidden="true"
                                                            className="h-3 w-3"
                                                        />
                                                        Edit
                                                    </button>
                                                    <button
                                                        data-testid={`archive-${v.slug}`}
                                                        type="button"
                                                        onClick={() =>
                                                            archiveVideo(v)
                                                        }
                                                        className="inline-flex items-center gap-1 rounded-full border border-rose-500/40 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-rose-200 hover:bg-rose-500/15"
                                                    >
                                                        <Trash2
                                                            aria-hidden="true"
                                                            className="h-3 w-3"
                                                        />
                                                        Archive
                                                    </button>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <p className="mt-6 text-xs text-brand-muted">
                    File limits: only <code>.mp4</code>, ideal length 30s–5min,
                    max {Math.round(VIDEO_MAX_BYTES / (1024 * 1024))} MB. All
                    uploads stream directly into Dropbox; the website serves
                    playback through our own origin so visitors never leave
                    endurancecasualty.com.
                </p>
            </section>

            {editing && (
                <VideoEditModal
                    video={editing}
                    categories={categories}
                    onClose={() => setEditing(null)}
                    onSaved={(updated) => {
                        setVideos((arr) =>
                            arr.map((v) =>
                                v.id === updated.id ? { ...v, ...updated } : v
                            )
                        );
                        setEditing(null);
                    }}
                />
            )}
            {creating && (
                <VideoCreateModal
                    categories={categories}
                    onClose={() => setCreating(false)}
                    onCreated={(created) => {
                        setVideos((arr) => [created, ...arr]);
                        setCreating(false);
                    }}
                />
            )}
        </main>
    );
}
